/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { getLanguageMatch } from 'js/lib/language';

import { Typography2 } from '@coursera/cds-core';

import type { CourseLanguages } from 'bundles/common/queries/useGetCourseLanguages';
import TranslationMenuItem from 'bundles/composable-page-header/components/translation/TranslationMenuItem';
import type { Language } from 'bundles/composable-page-header/legacy/translation/useCourseLanguageQuery';

import _t from 'i18n!nls/composable-page-header';

const styles = {
  groupLabel: css`
    padding: 0 var(--cds-spacing-150);
    column-span: all;
  `,
  groupSection: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  languageGrid: css`
    column-count: 3;
    column-gap: var(--cds-spacing-200);
  `,
};

type Props = {
  languages: Language[];
  currentLanguage: string;
  courseLanguages: CourseLanguages;
};

const TranslationMenuWithGrouping = ({ languages, currentLanguage, courseLanguages }: Props) => {
  const availableLanguages = useMemo(
    () =>
      languages.filter((language) => {
        const { autoTranslations, humanTranslations, originalLanguage } = courseLanguages;
        return (
          !!getLanguageMatch(language.key, originalLanguage) ||
          autoTranslations.find((translation) => !!getLanguageMatch(language.key, translation)) ||
          humanTranslations.find((translation) => !!getLanguageMatch(language.key, translation))
        );
      }),
    [courseLanguages, languages]
  );

  const subtitleLanguages = useMemo(
    () =>
      languages.filter((language) => {
        const { subtitles } = courseLanguages;
        return (
          subtitles.find((translation) => !!getLanguageMatch(language.key, translation)) &&
          !availableLanguages.find((availableLanguage) => availableLanguage.key === language.key)
        );
      }),
    [courseLanguages, languages, availableLanguages]
  );

  if (availableLanguages.length === 0) {
    return null;
  }

  return (
    <>
      <div css={styles.groupSection}>
        <Typography2 variant="subtitleMedium" component="h3" css={styles.groupLabel}>
          {_t('Available languages')}
        </Typography2>
        <div role="listbox" css={styles.languageGrid}>
          {availableLanguages.map((language) => (
            <TranslationMenuItem key={language.key} language={language} currentLanguage={currentLanguage} />
          ))}
        </div>
      </div>
      {subtitleLanguages.length > 0 && (
        <div css={styles.groupSection}>
          <Typography2 variant="subtitleMedium" component="h3" css={styles.groupLabel}>
            {_t('Limited translation')}
          </Typography2>

          <div role="listbox" css={styles.languageGrid}>
            {subtitleLanguages.map((language) => (
              <TranslationMenuItem key={language.key} language={language} currentLanguage={currentLanguage} />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default TranslationMenuWithGrouping;
